import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import EditorField from '../Editor/editor-field';
import { checkValueType, parseValue, parseNumber } from '../Editor/util';
import EditorLabel from '../Editor/EditorLabel';
import EditorContainer from '../Editor/EditorContainer';
import EditorJSON from '../Editor/EditorJSON';
import ErrorContainer from '../Editor/ErrorContainer';
import { useAppState } from '../../hooks';
import { InputV2 } from '../v2';
import { ElidableText } from '..';
import { InputShape } from '../v2/input';

const ATTRIBUTE_WIDTH = 3;
const SPACING = 2;

function AttributeFunction(props) {
  const {
    isEditing,
    temporaryObject,
    customAttributes,
    faasAttributes,
    validateObject,
    themeMode,
    setSelectedObject,
  } = props;

  const { isCcsV2 } = useAppState();

  // TODO: On new ccs v2 faas, we want to show string
  const valueType = isCcsV2
    ? checkValueType(temporaryObject.payload.body.parameters)
    : checkValueType(temporaryObject.payload.body.payload);

  const isAttribute = valueType === 'attribute';
  const isBoolean = valueType === 'boolean';
  const isCustomJSON = valueType === 'json';
  const isErroredFaasId = temporaryObject.errorArray && temporaryObject.errorArray.includes('payload.faasId');
  const isErrored = isCcsV2
    ? temporaryObject.errorArray && temporaryObject.errorArray.includes('payload.body.parameters')
    : temporaryObject.errorArray && temporaryObject.errorArray.includes('payload.body.payload');

  const selectItem = (item, type) => {
    // Update the temporary object with the new information
    const newObject = { ...temporaryObject };
    if (type === 'number') {
      if (isCcsV2) {
        temporaryObject.payload.body.parameters = parseNumber(item);
      } else {
        temporaryObject.payload.body.payload = parseNumber(item);
      }
    } else if (isCcsV2) {
      temporaryObject.payload.body.parameters = item;
    } else {
      temporaryObject.payload.body.payload = item;
    }
    validateObject(newObject);
  };

  const selectFaasItem = (item) => {
    // Update the temporary object with the new information
    const newObject = { ...temporaryObject };
    newObject.payload.faasId = item.id;
    validateObject(newObject);
  };

  const selectValueType = (type) => {
    switch (type) {
      case 'attribute':
        selectItem('{{}}', type);
        break;
      case 'json':
        selectItem({}, type);
        break;
      case 'boolean':
        selectItem(true, type);
        break;
      case 'number':
        selectItem(0, type);
        break;
      default:
        selectItem('', type);
        break;
    }
  };

  const handleTextAreaChange = (event) => {
    // Set an unparsed string in the selectedObject that can be validated on save
    setSelectedObject(event.target.value);

    try {
      const parsedJSON = JSON.parse(event.target.value, undefined, 4);
      selectItem(parsedJSON, valueType);
    } catch (error) {
      // addNotification(error);
    }
  };

  const getFaasAttribute = () => {
    // Check for an existing faasId in the payload
    if (faasAttributes.length && temporaryObject.payload.faasId) {
      const foundFaasIndex = faasAttributes.findIndex(
        (item) => item.id === temporaryObject.payload.faasId,
      );
      if (foundFaasIndex > -1) {
        return faasAttributes[foundFaasIndex].name;
      }
    }
    // if it doesn't exist, then return an empty string
    return '';
  };

  const getPayloadComponent = () => {
    const payloadValue = isCcsV2
      ? parseValue(valueType, temporaryObject.payload.body.parameters)
      : parseValue(valueType, temporaryObject.payload.body.payload);

    if (isAttribute) {
      return (
        <Box mr={SPACING} flex={isEditing ? 3 : 1} height="32px">
          <EditorField
            isEditing={isEditing}
            value={payloadValue}
            selectItem={(item) => selectItem(`{{${item}}}`, valueType)}
            items={customAttributes}
            placeholder="Choose an attribute"
            isErrored={isErrored}
          />
        </Box>
      );
    }

    if (isBoolean) {
      return (
        <Box mr={SPACING} flex={isEditing ? 3 : 1} height="32px">
          <EditorField
            isEditing={isEditing}
            value={payloadValue}
            selectItem={(item) => selectItem(item, valueType)}
            items={[true, false]}
            noFreeText
            isErrored={isErrored}
          />
        </Box>
      );
    }

    if (isCustomJSON) {
      return (
        <ErrorContainer
          isEditing={isEditing}
          isErrored={isErrored}
        >
          <EditorJSON
            isEditing={isEditing}
            themeMode={themeMode}
            jsonObject={temporaryObject.payload.body.payload
               || temporaryObject.payload.body.parameters}
            jsonString={JSON.stringify(temporaryObject.payload.body.payload
               || temporaryObject.payload.body.parameters, undefined, 4)}
            handleChange={handleTextAreaChange}
          />
        </ErrorContainer>
      );
    }

    return isEditing ? (
      <Box height="32px" width="100%">
        <InputV2 value={payloadValue} placeholder="Enter a value" onChange={(event) => selectItem(event.target.value, valueType)} isErrored={isErrored} testId="editor-text" shape={InputShape.SQUARE} />
      </Box>
    ) : (
      <Text variant="primary" data-testid="editor-text">
        <ElidableText text={payloadValue} lineCount={1} />
      </Text>
    );
  };

  const faasAttribute = getFaasAttribute();

  return (
    <>
      <EditorContainer variant="inner" p={3} mt={3} data-testid="editor-function">
        <EditorLabel>Function</EditorLabel>
        <Box flex={ATTRIBUTE_WIDTH} height="32px">
          <EditorField
            isEditing={isEditing}
            value={faasAttribute}
            selectItem={(item) => selectFaasItem(item)}
            items={faasAttributes}
            noFreeText
            actionType="faas"
            isErrored={isErroredFaasId}
          />
        </Box>
      </EditorContainer>
      <EditorContainer variant="inner" p={3} mt={3} data-testid="editor-parameters">
        <EditorLabel>Parameters</EditorLabel>
        <Flex alignItems="flex-start">
          {isEditing && (
            <Box mr={SPACING} minWidth={150}>
              <EditorField
                isEditing
                value={valueType}
                selectItem={selectValueType}
                items={['string', 'attribute', 'boolean', 'number', 'json']}
                noFreeText
              />
            </Box>
          )}
          {getPayloadComponent()}
        </Flex>
      </EditorContainer>
    </>
  );
}

AttributeFunction.propTypes = {
  faasAttributes: PropTypes.array.isRequired,
  customAttributes: PropTypes.array.isRequired,
  isEditing: PropTypes.bool.isRequired,
  temporaryObject: PropTypes.object.isRequired,
  validateObject: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  setSelectedObject: PropTypes.func.isRequired,
};

export default AttributeFunction;
